/*******************************************************************
* PAGE        : AppRoutes
* REMARKS     : Comman App Routes
* CREATED ON  : 03-11-2025
* AUTHOR      : Bamroliya.Paras
* MODIFY      : Bamroliya.Paras
* MODIFY ON   : 20-03-2026
********************************************************************/

import { lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router-dom';

import MainLayout from './MainLayout';

import LoaderComponent from '../components/LoaderComponent/LoaderComponent';
import AmcWarrantyManagementList from '../pages/AmcWarrantyManagementPage/AmcWarrantyManagementList';
import useRestoreSession from '../store/useRestoreSession';

// Lazy loading components for routes
const DashboardBuilder = lazy(() => import('../components/DashboardBuilder/DashboardBuilder'));
const ChildCharts = lazy(() => import('../components/DashboardBuilder/ChildCharts'));
const HomePage = lazy(() => import('../pages/HomePage'));
const ComplaintListPage = lazy(() => import('../pages/ComplaintPage/ComplaintListPage'));
const AssignManuallyPage = lazy(() => import('../pages/AssignManuallyPage/AssignManuallyPage'));
const DocumentationPage = lazy(() => import('../pages/DocumentationPage/DocumentationPage'));
const TechnicianManagementPage = lazy(() => import('../pages/TechnicianManagementPage/TechnicianPage'));
const AnalyticsReports = lazy(() => import('../pages/AnalyticsReports/AnalyticsReports'));
const ReimbursementListPage = lazy(() => import('../pages/ReimbursementListPage/ReimbursementListPage'));
const SetRightsPage = lazy(() => import('../pages/SetRightsPage/SetRightsPage'));
const UsersListPage = lazy(() => import('../pages/UsersPage/UsersListPage'));
const PreChecklistQuestionsList = lazy(() => import('../pages/PreChecklistQuestions/PreChecklistQuestionsList'));
const FeedbackQuestionsList = lazy(() => import('../pages/FeedbackQuestions/FeedbackQuestionsList'));
const SettingsPage = lazy(() => import('../pages/SettingsPage/SettingsPage'));
const ProfilePage = lazy(() => import('../pages/ProfilePage/ProfilePage'));
const NotificationPage = lazy(() => import('../pages/NotificationPage/NotificationPage'));
const CAPAAnalysis = lazy(() => import('../pages/CAPAA/CAPAAnalysis'));
const CustomerFeedback = lazy(() => import('../pages/CustomerFeedback/CustomerFeedback'));
const AnnouncementManagement = lazy(() => import('../pages/AnnouncementManagement/AnnouncementManagement'));
const LeaveManagementList = lazy(() => import('../pages/LeaveManagementPage/LeaveManagementList'));


const LoginPage = lazy(() => import('../pages/LoginPage/LoginPage'));

const staticAllowedRoutes = [
    '/home',
    '/profile',
    '/settings',
    '/notification',
    '/dashboard/childcharts',
];

const protectedRoutes = [
    { path: "/dashboard", element: <DashboardBuilder /> },
    { path: "/dashboard/childcharts", element: <ChildCharts /> },
    { path: "/home", element: <HomePage /> },
    { path: "/complaints", element: <ComplaintListPage /> },
    { path: "/complaints/new", element: <ComplaintListPage /> },
    { path: "/complaints/assigned", element: <ComplaintListPage /> },
    { path: "/complaints/unassigned", element: <ComplaintListPage /> },
    { path: "/complaints/pending", element: <ComplaintListPage /> },
    { path: "/complaints/acceptedrejected", element: <ComplaintListPage /> },
    { path: "/complaints/rejected", element: <ComplaintListPage /> },
    { path: "/assignmanually", element: <AssignManuallyPage /> },
    { path: "/documentation", element: <DocumentationPage /> },
    { path: "/technicians", element: <TechnicianManagementPage /> },
    { path: "/analyticsreports", element: <AnalyticsReports /> },
    { path: "/settings", element: <SettingsPage /> },
    { path: "/profile", element: <ProfilePage /> },
    { path: "/reimbursement", element: <ReimbursementListPage /> },
    { path: "/users", element: <UsersListPage /> },
    { path: "/prechecklistquestions", element: <PreChecklistQuestionsList /> },
    { path: "/feedbackquestions", element: <FeedbackQuestionsList /> },
    { path: "/setrights", element: <SetRightsPage /> },
    { path: "/notification", element: <NotificationPage /> },
    { path: "/amcwarrantymanagement", element: <AmcWarrantyManagementList /> },
    { path: "/capaanalysis", element: <CAPAAnalysis /> },
    { path: "/announcement", element: <AnnouncementManagement /> },
    { path: "/customerfeedback", element: <CustomerFeedback /> },
    { path: "/leavemanagement", element: <LeaveManagementList /> },
];

const AppRoutes = () => {
    const { isAuthenticated, allowedRoutes } = useSelector(state => state.reduxMaster);
    const isRestoringSession = useRestoreSession();

    if (isRestoringSession) {
        return <LoaderComponent />;
    }

    const ProtectedRoute = ({ children }) => {
        if (!isAuthenticated) {
            return <Navigate to="/login" replace />;
        }

        return children;
    };

    const finalAllowedRoutes = [
        ...allowedRoutes,
        ...staticAllowedRoutes
    ];

    //  FILTER ROUTES USING API
    const filteredRoutes = protectedRoutes.filter(route =>
        finalAllowedRoutes.some(allowed => route.path.startsWith(allowed))
    );

    return (
        <Suspense fallback={<LoaderComponent />}>
            <Routes>

                {/* AUTHENTICATED LAYOUT */}
                {isAuthenticated && (
                    <Route
                        path="/*"
                        element={
                            <ProtectedRoute>
                                <MainLayout>
                                    <Routes>
                                        {filteredRoutes.map(({ path, element }) => (
                                            <Route key={path} path={path} element={element} />
                                        ))}

                                        {/* AUTH FALLBACK */}
                                        <Route
                                            path="*"
                                            element={
                                                allowedRoutes.length > 0
                                                    ? <Navigate to={allowedRoutes[0]} replace />
                                                    : <div />  // empty content, layout still loads
                                            }
                                        />
                                    </Routes>
                                </MainLayout>
                            </ProtectedRoute>
                        }
                    />
                )}

                {/* PUBLIC */}
                {!isAuthenticated && (
                    <Route path="/login" element={<LoginPage />} />
                )}

                {/* FALLBACK */}
                <Route path="*" element={
                    isAuthenticated
                        ? <Navigate to={allowedRoutes[0] || '/dashboard'} replace />
                        : <Navigate to="/login" replace />}
                />

            </Routes>
        </Suspense>

    );
};

export default AppRoutes;
